import { useDraggable } from '@dnd-kit/core';
import classNames from 'classnames';
import { CSS } from '@dnd-kit/utilities';
import { Typography } from 'antd';
import { AppFlex } from 'shared/UI/UIKit/Flex';
import { AppLockedIcon } from 'shared/UI/UIKit/icons/Locked';
import { AppButton } from 'shared/UI/UIKit/Button';
import { AppLockOffIcon } from 'shared/UI/UIKit/icons/LockOff';
import { AppEditIcon } from 'shared/UI/UIKit/icons/Edit';
import { AppUsersGroupIcon } from 'shared/UI/UIKit/icons/UsersGroup';
import { AppTooltip } from 'shared/UI/UIKit/Tooltip';
import type { EducationClass, ScheduleErrorTimeslot, ScheduleLesson } from 'services/api';
import { useLessonCard } from '../model';
import { LessonModal } from '../../LessonModal';
import styles from './styles.module.scss';

interface LessonCardProps {
  lesson: ScheduleLesson;
  slotKey: string;
  errors?: ScheduleErrorTimeslot[];
  educationalClass?: EducationClass;
}

export const LessonCard = ({ lesson, slotKey, errors, educationalClass }: LessonCardProps) => {
  const {
    state: {
      cardStyle,
      isEditMode,
      isPinChanged,
      isRoomChanged,
      isTimeslotChanged,
      isTeacherChanged,
      editLessonModalOpen,
    },
    methods: {
      handleOpenLessonDetails,
      handleTogglePinned,
      handleOpenEditLessonModal,
      handleCloseEditLessonModal,
    },
  } = useLessonCard(lesson);

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `${slotKey}-${lesson.id}`,
    data: { slotKey, lessonId: lesson.id },
    disabled: !isEditMode || lesson?.pinned,
  });

  return (
    <>
      <div
        ref={setNodeRef}
        data-lesson-id={lesson.id}
        className={classNames(styles.card, {
          [styles.cardDragging]: isDragging,
        })}
        style={transform ? { transform: CSS.Translate.toString(transform) } : undefined}
        {...listeners}
        {...attributes}
        onClick={() => handleOpenLessonDetails(lesson, slotKey, errors, educationalClass)}
      >
        <AppFlex direction="row" unit="16" alignItems="center">
          <div
            className={classNames(styles.cardContent, {
              [styles.cardMoved]: isTimeslotChanged,
            })}
            style={cardStyle}
          >
            {(isEditMode || lesson.pinned) && (
              <AppButton
                type="text"
                className={classNames(styles.pinButton, {
                  [styles.pinButtonVisible]: lesson.pinned || isPinChanged,
                  [styles.pinButtonChanged]: isPinChanged,
                })}
                icon={
                  lesson.pinned ? (
                    <AppLockedIcon className={styles.pinned} />
                  ) : (
                    <AppLockOffIcon className={styles.pinned} />
                  )
                }
                onClick={(event) => {
                  event.stopPropagation();
                  handleTogglePinned();
                }}
                onPointerDown={(event) => {
                  event.stopPropagation();
                }}
              />
            )}

            {isEditMode && (
              <AppButton
                type="text"
                className={classNames(styles.editButton)}
                icon={<AppEditIcon className={styles.editIcon} />}
                onClick={(event) => {
                  event.stopPropagation();
                  handleOpenEditLessonModal();
                }}
                onPointerDown={(event) => {
                  event.stopPropagation();
                }}
              />
            )}

            <AppFlex unit="4" className={styles.cardContentWrapper}>
              <Typography.Text className={styles.subjectName}>
                {lesson.subject.name}
              </Typography.Text>
              <Typography.Text
                type="secondary"
                className={classNames(styles.teacherName, {
                  [styles.teacherChanged]: isTeacherChanged,
                })}
              >
                {lesson.teacher.code}
              </Typography.Text>
            </AppFlex>

            <div className={classNames(styles.roomNumber, { [styles.roomChanged]: isRoomChanged })}>
              Кабинет {lesson?.room.number}
              {lesson?.group && (
                <AppTooltip title="Урок по группам">
                  <AppUsersGroupIcon />
                </AppTooltip>
              )}
            </div>
          </div>
        </AppFlex>
      </div>

      <LessonModal
        open={editLessonModalOpen}
        onClose={handleCloseEditLessonModal}
        lesson={lesson}
      />
    </>
  );
};
